import { find, intersection, values } from 'lodash'
import getCategorySingleton from '../helpers/categories'

export default class CatFilter {
  constructor () {
    this._filter = document.getElementById('cat-filter')
    this._type = intersection(values(CatFilter.TYPE), this._filter.classList)?.[0]
    this.filter = { search_category: undefined }

    if (!this._type) {
      throw new Error(`invalid CatFilter type ${this._filter.classList}`)
    }
  }

  static get TYPE () {
    return {
      STANDARD: 'standard',
      RADIO: 'radio'
    }
  }

  static get EVENT () {
    return {
      CHANGE: 'poin:catFilter:change',
      OPEN: 'poin:catFilter:open',
      CLOSE: 'poin:catFilter:close'
    }
  }

  async init () {
    this.categorySingleton = await getCategorySingleton()

    switch (this._type) {
      case CatFilter.TYPE.STANDARD:
        this._filter.addEventListener('click', () => this._openDropdown())
        this._dropdown = document.getElementById('cat-filter-dropdown')
        this.categorySingleton.categories.forEach((_cat) => {
          const option = document.createElement('p')
          option.textContent = _cat.human_name
          option.className = 'cat-option'
          option.setAttribute('data-value', _cat.id)
          this._dropdown.appendChild(option)
        })
        break
      case CatFilter.TYPE.RADIO: {
        this._list = document.getElementById('cat-filter-list')
        this._list.classList.remove('hidden')
        this.categorySingleton.categories.forEach((_cat) => {
          const container = document.createElement('div')
          const option = document.createElement('input')
          const label = document.createElement('label')

          option.type = 'radio'
          option.id = `${_cat.human_name}-${_cat.id}`
          option.className = 'cat-radio'
          option.name = 'cat'
          option.value = _cat.id
          option.addEventListener('change', (event) => {
            for (const element of this._list.getElementsByTagName('label')) {
              element.style.fontWeight = element.htmlFor === event.target.id
                ? 600
                : 400
            }
            this.filter.search_category = this.categorySingleton.SEOName(event.target.value)
            this._filter.dispatchEvent(new Event(CatFilter.EVENT.CHANGE, { bubbles: true }))
          })

          label.htmlFor = option.id
          label.textContent = _cat.human_name

          container.classList.add('cat-option')
          container.appendChild(option)
          container.appendChild(label)
          this._list.appendChild(container)
        })

        const blankOption = document.createElement('input')
        const container = document.createElement('div')
        blankOption.type = 'radio'
        blankOption.id = 'blank_option'
        blankOption.className = 'cat-radio'
        blankOption.name = 'cat'
        blankOption.value = 'blank_option'
        container.style.display = 'none'
        container.appendChild(blankOption)
        this._list.appendChild(container)

        break
      }
    }
    const queryParams = new URLSearchParams(window.location.search)
    const cat = queryParams.get('search_category')

    if (cat) {
      this.filter.search_category = cat
      const id = this.categorySingleton.idFromSEO(cat)

      switch (this._type) {
        case CatFilter.TYPE.STANDARD: {
          const element = find(document.querySelectorAll('.cat-option'),
            (_element) => _element.getAttribute('data-value') === id.toString())

          if (element) {
            element.classList.add('selected')
            const catFilterP = this._filter.getElementsByTagName('p')[0]
            catFilterP.textContent = element.textContent
            catFilterP.classList.add('selected')
          }
          break
        }
        case CatFilter.TYPE.RADIO: {
          const element = find(document.querySelectorAll('.cat-radio'),
            (_element) => _element.getAttribute('value') === id.toString())

          if (element) {
            element.checked = true
            const label = find(this._list.querySelectorAll('label'), (_element) => _element.htmlFor === element.id)
            if (label) { label.style.fontWeight = 600 }
          }
          break
        }
      }
    }
  }

  _openDropdown () {
    this._dropdown.classList.toggle('hidden')
    const viewportWidth = document.documentElement.clientWidth
    const horizontalDisplacement = this._filter.offsetLeft - this._dropdown.offsetLeft - this._filter.parentElement.scrollLeft
    const verticalDisplacement = this._filter.offsetTop - this._dropdown.offsetTop + this._filter.offsetHeight + 5
    /*
      the dropdown is now set to be positioned initially at 0,0 and translated afterwards to prevent
      the navbar overflowing the viewport width, the same behaviour has been implemented in the dropdown component
    */
    if (this._dropdown.classList.contains('hidden')) {
      this._filter.dispatchEvent(new Event(CatFilter.EVENT.CLOSE, { bubbles: true }))
      this._dropdown.style.removeProperty('transform')

      this._removeOptionListeners()
    } else {
      this._filter.dispatchEvent(new Event(CatFilter.EVENT.OPEN, { bubbles: true }))
      this._dropdown.style.transform = `translate(${horizontalDisplacement}px, ${verticalDisplacement}px)`

      if (this._dropdown.getBoundingClientRect().right > viewportWidth) {
        const additionalHorizontalOffset = this._dropdown.getBoundingClientRect().right - viewportWidth + 5
        this._dropdown.style.transform = `translate(${horizontalDisplacement - additionalHorizontalOffset}px, ${verticalDisplacement}px)`
      } else if (this._dropdown.getBoundingClientRect().left < 0) {
        this._dropdown.style.transform = `translate(5px, ${verticalDisplacement}px)`
      }

      this._addOptionListeners()
    }
  }

  close () {
    if (this._type === CatFilter.TYPE.STANDARD) { this._dropdown.classList.add('hidden') }
    this._removeOptionListeners()
  }

  filterIsSet () {
    return Boolean(this.filter.search_category)
  }

  clean () {
    switch (this._type) {
      case CatFilter.TYPE.STANDARD:
        break
      case CatFilter.TYPE.RADIO: {
        const option = find(document.querySelectorAll('.cat-radio'), (element) => element.id === 'blank_option')
        option.checked = true
        for (const element of this._list.getElementsByTagName('label')) { element.style.fontWeight = 400 }
        break
      }
    }
    this.filter.search_category = undefined
    this._filter.dispatchEvent(new Event(CatFilter.EVENT.CHANGE, { bubbles: true }))
  }

  _addOptionListeners () {
    for (const option of document.querySelectorAll('.cat-option')) {
      option.addEventListener('click', this._selectDropdownElement.bind(this))
    }
  }

  _removeOptionListeners () {
    for (const option of document.querySelectorAll('.cat-option')) {
      option.removeEventListener('click', this._selectDropdownElement.bind(this))
    }
  }

  _selectDropdownElement (event) {
    if (!event.target.classList.contains('selected')) {
      const prevSelected = event.target.parentNode.querySelector('.cat-option.selected')

      if (prevSelected) { prevSelected.classList.remove('selected') }

      const catFilterP = this._filter.getElementsByTagName('p')?.[0]
      catFilterP.textContent = event.target.textContent
      catFilterP.classList.add('selected')
      event.target.classList.add('selected')

      this.filter.search_category = this.categorySingleton.SEOName(event.target.dataset.value)
      this._filter.dispatchEvent(new Event(CatFilter.EVENT.CHANGE, { bubbles: true }))
    }
  }
}
